import { IconClipboardList } from '@tabler/icons-react';
import { Apple, ArrowLeftToLine, ArrowRightFromLine, X, Zap } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { DeliveryState, TaskState } from 'data/__generated__';

import { formatDurationMinutes } from 'helpers/dateTimes';
import { colors } from 'constants/colors';
import { UNASSIGNED_COLOR } from 'pages/DynamicDeliveryPlanning/constants/approColors';
import { useTourRoutes } from 'pages/DynamicDeliveryPlanning/hooks/useTourRoutes';

import {
    STATUT_COLOR,
    UNASSIGNED_STATUT_COLOR,
    DEPOT_ROAD_FALLBACK_MINUTES,
    DEPOT_SAS_MINUTES,
    GENNEVILLIERS_DEPOT,
    deliveryHref,
    findStop,
    tourHref,
    formatMinutes,
    formatWindow,
    stopWindow,
    parseTimeToMinutes,
    tourStateLabel,
    TASK_STATE_LABELS,
    deliveryContractLabel,
    deliveryLabel,
    deliveryWindow,
    deliveryZoneName,
    deliveryContentSummary,
    deliveryFullLabel,
    getStopState,
    hasFreefood,
    isTask,
    orderedStops,
    stopAddress,
    stopCoordinates,
    stopLabel,
    taskLabel,
    tourOrigin,
    windowBounds,
    type Tour,
    type TourDelivery,
    type TourStopItem,
    type TourTask,
} from '../helpers';
import type { TourActions } from '../hooks/useTourActions';
import { ApproPicker, type ApproOption } from './ApproPicker';
import { OriginIcon } from './OriginIcon';
import { STATE_TONE, TASK_STATE_TONE, toneColor, type StateTone } from './StopStatus';
import { TaskIcon } from './TaskStop';
import {
    CHIP_GAP,
    CHIP_LABEL_MAX_PX,
    DOT,
    buildSchedule,
    depotFlagLeftPx,
    depotMarks,
    layoutLane,
    type DepotMarks,
    type Placed,
    type Scheduled,
} from './timeline-layout';
import { DELIVERY_STATES_LABELS } from 'pages/Deliveries/constants/states';

const PAD_PCT = 1; // % inset so the first/last chips never clip at the track edges (the layout pass keeps every chip inside the track — dots overlap rather than spill past the right edge)

const CHIP_FONT_SIZE_PX = 10;
const CHIP_FONT_WEIGHT = 700;
/** Chrome reserved around the text: the 2 x 5 px CSS padding plus the 2 x 2 px border, plus 2 px of slack. */
const CHIP_PADDING_PX = 16;

/**
 * Real rendered width of a name, measured once per label in a canvas at the chip's own font. An « average
 * character width » estimate under-measured every uppercase site, which then wore an ellipsis on a lane that
 * had room to spare.
 */
const measureLabel = (() => {
    const context = document.createElement('canvas').getContext('2d');
    const cache = new Map<string, number>();

    return (label: string): number => {
        const cached = cache.get(label);
        if (cached != null) return cached;
        if (!context) return label.length * 6.5; // no canvas (tests, SSR): the old estimate, on the safe side

        context.font = `${CHIP_FONT_WEIGHT} ${CHIP_FONT_SIZE_PX}px ${getComputedStyle(document.body).fontFamily}`;
        const width = Math.ceil(context.measureText(label).width);
        cache.set(label, width);

        return width;
    };
})();

/** Room for one icon after the name: the dynamic bolt, the freefood apple. */
const CHIP_ICON_PX = 13;

/** The hover cards' shared text run: « · Livrée · fixe · 25 min sur place », after the card-specific lead. */
const stateMeta = (delivery: TourDelivery): string =>
    ` · ${DELIVERY_STATES_LABELS[getStopState(delivery)]}${delivery.isDynamic ? ' · dynamique' : ' · fixe'} · ${formatDurationMinutes(delivery.theoricServiceTime ?? 0)} sur place`;

/** What the chip spells out: its rank WITHIN ITS TOUR, then the site. */
const chipLabel = (item: Scheduled): string => `${item.seq}. ${stopLabel(item.stop)}`;

const chipIconsWidth = (stop: Scheduled['stop']): number =>
    isTask(stop) ? CHIP_ICON_PX : (stop.isDynamic ? CHIP_ICON_PX : 0) + (hasFreefood(stop) ? CHIP_ICON_PX : 0);

const chipWidth = (item: Scheduled): number =>
    Math.min(CHIP_LABEL_MAX_PX, measureLabel(chipLabel(item)) + CHIP_PADDING_PX + chipIconsWidth(item.stop));

const DEPOT_POSITION: [number, number] = [GENNEVILLIERS_DEPOT.latitude, GENNEVILLIERS_DEPOT.longitude];

/** Hovered chip: the placed stop + its on-screen rect, so the fixed card can anchor to it (never clipped). */
type Hovered = { placed: Scheduled; rect: DOMRect };

/** A hovered warehouse leg: which tour, which end of the day, and whether the axis had to clamp it. */
type HoveredDepot = { tour: Tour; depot: DepotMarks; isReturn: boolean; isClamped: boolean; rect: DOMRect };

/** The lane a chip is being dragged from: a tour, or the « à affecter » lane. */
const LOOSE_LANE = 'loose';

/**
 * Dispatch timeline — a 4-column grid: Tournée · Appro · Statut · frise horaire. Everything fits on screen
 * (the frise uses % of the available width, no horizontal scroll). Every tour is editable the same way,
 * whatever the stream that composed it: rename, assign an appro, drag chips between lanes, drop a chip back
 * on « à affecter ». Chips sit at their estimated passage time; hover shows a card; click opens the delivery.
 */
export type DraftTour = { key: string; name: string };

export const TourTimeline = ({
    tours,
    unassignedDeliveries,
    unassignedTasks,
    onOpenTask,
    isToday,
    deliveryPersonOptions,
    actions,
    draftTours,
    setDraftTours,
    matchingStopIds,
}: {
    tours: Tour[];
    /** Deliveries with no tour yet — the « à affecter » lane, a drag source AND a drop target. */
    unassignedDeliveries: TourDelivery[];
    unassignedTasks: TourTask[];
    onOpenTask: (task: TourTask) => void;
    /** The "now" line only makes sense when the displayed date is the current day. */
    isToday: boolean;
    /** Appro choices, labels already carrying the day's load («… — 2 tournées · 08:30 ⚠»). */
    deliveryPersonOptions: ApproOption[];
    actions: TourActions;
    draftTours: DraftTour[];
    setDraftTours: React.Dispatch<React.SetStateAction<DraftTour[]>>;
    /** Stops kept by the page filters (null = no filter): the others are dimmed in place, never moved nor hidden. */
    matchingStopIds: Set<string> | null;
}) => {
    const navigate = useNavigate();
    const { search } = useLocation();
    const [hovered, setHovered] = useState<Hovered | null>(null);
    const [hoveredDepot, setHoveredDepot] = useState<HoveredDepot | null>(null);
    /** « À affecter » chips carry no schedule, so they get their own card — what the dispatcher needs to
        decide WHERE to drop them: créneau, zone, charge sur place, caisses, adresse. */
    const [hoveredLoose, setHoveredLoose] = useState<{ delivery: TourDelivery; rect: DOMRect } | null>(null);
    const [editingTourId, setEditingTourId] = useState<string | null>(null);
    const [dragging, setDragging] = useState<{ stopId: string; fromKey: string } | null>(null);
    const [dropTarget, setDropTarget] = useState<string | null>(null);
    /** Where a dragged chip would land in the hovered lane: the insertion rank plus the px to draw the caret. */
    const [dropAt, setDropAt] = useState<{ key: string; index: number; leftPx: number } | null>(null);
    /**
     * Real px width of the time track (the grid's last column) — everything below is laid out in px.
     * Callback ref rather than useRef + useEffect: it fires the moment the node is attached, so the first
     * paint already has a width instead of a frame where every chip thinks the track is 0 wide. It is
     * attached to the FIRST lane's track — the box the chips actually live in.
     */
    const [trackWidth, setTrackWidth] = useState(0);
    const observerRef = useRef<ResizeObserver | null>(null);

    // Real road time depot → first stop and last stop → depot (IGN, cached by the hook); a lane without
    // coordinates or without a route falls back to the flat DEPOT_ROAD_FALLBACK_MINUTES.
    const depotLegs = useMemo(
        () =>
            tours.flatMap((tour) => {
                const stops = orderedStops(tour);
                const first = stops[0] ? stopCoordinates(stops[0]) : null;
                const last = stops[stops.length - 1] ? stopCoordinates(stops[stops.length - 1]) : null;
                return [
                    ...(first
                        ? [
                              {
                                  approId: `${tour._id}:out`,
                                  positions: [DEPOT_POSITION, [first.latitude, first.longitude] as [number, number]],
                              },
                          ]
                        : []),
                    ...(last
                        ? [
                              {
                                  approId: `${tour._id}:back`,
                                  positions: [[last.latitude, last.longitude] as [number, number], DEPOT_POSITION],
                              },
                          ]
                        : []),
                ];
            }),
        [tours],
    );
    const depotRoutes = useTourRoutes(depotLegs);
    const roadMinutes = (key: string): number => {
        const route = depotRoutes.get(key);
        return route ? Math.round(route.durationSeconds / 60) : DEPOT_ROAD_FALLBACK_MINUTES;
    };
    const trackRef = useCallback((node: HTMLDivElement | null) => {
        observerRef.current?.disconnect();
        if (!node) return;
        setTrackWidth(node.getBoundingClientRect().width);
        observerRef.current = new ResizeObserver(([entry]) => setTrackWidth(entry.contentRect.width));
        observerRef.current.observe(node);
    }, []);

    if (tours.length === 0 && unassignedDeliveries.length === 0 && unassignedTasks.length === 0) {
        // Nothing to plan — or only tours the Statut filter leaves out (archived ones by default).
        return (
            <Empty>
                Aucune tournée à afficher pour cette date. Les tournées archivées n'apparaissent qu'en cochant «
                Archivée » dans Statut.
            </Empty>
        );
    }

    // The axis only measures TOURS — the « à affecter » pool is not a tour and must not stretch it
    // (88 loose deliveries once inflated the schedule to a ~40 h axis with unreadable hour ticks).
    const allMins = tours
        .flatMap((tour) => orderedStops(tour))
        .flatMap((stop) =>
            [parseTimeToMinutes(stopWindow(stop).start), parseTimeToMinutes(stopWindow(stop).stop)].filter(
                (value): value is number => value != null,
            ),
        );
    const windowsMin = Math.floor((allMins.length ? Math.min(...allMins) : 6 * 60) / 60) * 60;
    // The axis spans the DELIVERY WINDOWS, never the cumulative estimate: letting a tour whose estimate runs
    // to 17 h stretch the axis wasted the whole track and squashed every other lane to the left. A delivery
    // estimated past the last window is pinned to the axis end and stays red (`late`) — the exact time is in
    // its hover card, so nothing is lost, only the pixels.
    // No end margin: the layout pass already keeps every chip inside the track, so padding the axis only
    // bought an empty hour (a 14:00 window used to push the axis to 15h).
    const axisMax = Math.ceil((allMins.length ? Math.max(...allMins) : 19 * 60) / 60) * 60;
    const schedules = new Map(tours.map((tour) => [tour._id, buildSchedule(orderedStops(tour), windowsMin)]));
    const depots = new Map(
        tours.map((tour) => [
            tour._id,
            depotMarks(schedules.get(tour._id) ?? [], roadMinutes(`${tour._id}:out`), roadMinutes(`${tour._id}:back`)),
        ]),
    );
    // The axis starts AT the earliest depot departure (rounded down to 10 min): the lead is what the frise
    // shows on the left, not empty time before it (the windows all come after their tour's departure).
    const departures = [...depots.values()].flatMap((depot) => (depot ? [depot.departureMin] : []));
    const axisMin = Math.floor((departures.length ? Math.min(...departures) : windowsMin) / 10) * 10;
    const span = Math.max(axisMax - axisMin, 60);
    /** minutes → % across the frise, clamped to the axis, inset by PAD_PCT on both sides. */
    const xOf = (min: number): number =>
        PAD_PCT + ((Math.min(Math.max(min, axisMin), axisMax) - axisMin) / span) * (100 - 2 * PAD_PCT);

    /** Same scale as the ticks, in px — chips are placed and sized in px, the rest of the track stays in %. */
    const pxOf = (min: number): number => (xOf(min) / 100) * trackWidth;
    // Long day (overloaded tours): one tick every 2 h so the header stays readable.
    const hourStep = span > 15 * 60 ? 2 : 1;
    // Ticks on whole hours only — the axis itself may start on any 10-min mark.
    const firstHour = Math.ceil(axisMin / 60);
    const hours = Array.from(
        { length: Math.floor((axisMax - firstHour * 60) / 60 / hourStep) + 1 },
        (_, i) => firstHour + i * hourStep,
    );

    const now = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    const showNow = isToday && nowMin >= axisMin && nowMin <= axisMax;

    const endDrag = (): void => {
        setDragging(null);
        setDropTarget(null);
        setDropAt(null);
    };

    /**
     * Insertion rank under the cursor: the chips whose middle is already passed. The caret is snapped to the
     * middle of the gap it would fill, so the dispatcher sees the slot rather than their own mouse.
     */
    const dropPosition = (placed: Placed[], trackRect: DOMRect, clientX: number) => {
        const x = clientX - trackRect.left;
        const index = placed.filter((item) => item.leftPx + item.widthPx / 2 < x).length;
        const before = placed[index - 1];
        const after = placed[index];
        if (before && after) return { index, leftPx: (before.leftPx + before.widthPx + after.leftPx) / 2 };
        if (before) return { index, leftPx: before.leftPx + before.widthPx + CHIP_GAP };
        return { index, leftPx: Math.max(0, (after?.leftPx ?? x) - CHIP_GAP) };
    };

    const draggedStop = () =>
        dragging ? findStop(tours, unassignedDeliveries, unassignedTasks, dragging.stopId) : null;

    const isDimmed = (stop: TourStopItem): boolean => matchingStopIds !== null && !matchingStopIds.has(stop._id);

    // One draggable chip of a tour lane (the « à affecter » lane has its own wrapping LooseChip).
    const renderChip = (item: Placed, { fromKey }: { fromKey: string }) => {
        const { stop } = item;
        return (
            <Chip
                key={stop._id}
                $tone={isTask(stop) ? TASK_STATE_TONE[stop.state] : (STATE_TONE[getStopState(stop)] ?? 'neutral')}
                $late={item.late}
                $labelled={item.labelled}
                $square={isTask(stop)}
                $dragging={dragging?.stopId === stop._id}
                $dimmed={isDimmed(stop)}
                style={{ left: `${item.leftPx}px`, width: `${item.widthPx}px` }}
                draggable
                onDragStart={(event) => {
                    event.dataTransfer.effectAllowed = 'move';
                    setHovered(null);
                    setDragging({ stopId: stop._id, fromKey });
                }}
                onDragEnd={endDrag}
                onMouseEnter={(event) =>
                    setHovered({ placed: item, rect: event.currentTarget.getBoundingClientRect() })
                }
                onMouseLeave={() => setHovered(null)}
                onClick={() => (isTask(stop) ? onOpenTask(stop) : navigate(deliveryHref(stop._id)))}
                data-test={isTask(stop) ? 'tour-timeline-task' : undefined}
            >
                {item.labelled ? (
                    <>
                        <ChipLabel>{chipLabel(item)}</ChipLabel>
                        {isTask(stop) && <TaskIcon size={11} color="currentColor" />}
                        {!isTask(stop) && stop.isDynamic && <Zap size={11} fill={colors.violet} strokeWidth={0} />}
                        {!isTask(stop) && hasFreefood(stop) && <Apple size={11} strokeWidth={2.5} />}
                    </>
                ) : (
                    item.seq
                )}
            </Chip>
        );
    };

    /** The card hangs under its anchor, and flips above it near the bottom of the window. */
    const cardPosition = (rect: DOMRect) => ({
        left: rect.left + rect.width / 2,
        ...(rect.bottom > window.innerHeight - 150
            ? { bottom: window.innerHeight - rect.top + 8 }
            : { top: rect.bottom + 8 }),
    });

    const commitRename = (tour: Tour, value: string): void => {
        setEditingTourId(null);
        const name = value.trim();
        if (name && name !== tour.name) void actions.renameTour(tour, name);
    };

    const dropIntoDraft = async (draftKey: string, name: string): Promise<void> => {
        const dragged = draggedStop();
        endDrag();
        if (!dragged) return;
        const created = await actions.createTour([dragged.stop], name);
        if (created) setDraftTours((current) => current.filter((draft) => draft.key !== draftKey));
    };

    /** Drop handlers shared by every lane: prevent the default, highlight, then run `onDropped`. */
    const dropHandlers = (laneKey: string, canDrop: boolean, onDropped: () => void) =>
        canDrop
            ? {
                  onDragOver: (event: React.DragEvent) => {
                      event.preventDefault();
                      setDropTarget(laneKey);
                  },
                  onDragLeave: () => setDropTarget(null),
                  onDrop: (event: React.DragEvent) => {
                      event.preventDefault();
                      onDropped();
                      endDrag();
                  },
              }
            : {};

    return (
        <Wrap>
            <HeaderRow>
                <Th>Tournée</Th>
                <Th>Appro</Th>
                <Th>Statut</Th>
                <ThTrack>
                    {hours.map((hour) => (
                        <Tick key={hour} style={{ left: `${xOf(hour * 60)}%` }}>
                            {String(hour).padStart(2, '0')}h
                        </Tick>
                    ))}
                </ThTrack>
                <Th>Détails</Th>
            </HeaderRow>

            <Body>
                {tours.map((tour, laneIndex) => {
                    const schedule = schedules.get(tour._id) ?? [];
                    const placed = layoutLane(schedule, schedule.map(chipWidth), trackWidth, pxOf);
                    const depot = depots.get(tour._id) ?? null;
                    const { windowStartMin, windowStopMin } = windowBounds(orderedStops(tour));
                    const color = tour.color || UNASSIGNED_COLOR;
                    const statut = tourStateLabel(tour.state);
                    const droppable = Boolean(dragging);

                    return (
                        <Row key={tour._id} $drop={dropTarget === tour._id}>
                            <CellTour>
                                {editingTourId === tour._id ? (
                                    <NameInput
                                        autoFocus
                                        defaultValue={tour.name}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter') commitRename(tour, event.currentTarget.value);
                                            if (event.key === 'Escape') setEditingTourId(null);
                                        }}
                                        onBlur={(event) => commitRename(tour, event.currentTarget.value)}
                                    />
                                ) : (
                                    <Badge
                                        type="button"
                                        $color={color}
                                        $editable
                                        title="Cliquer pour renommer la tournée"
                                        onClick={() => setEditingTourId(tour._id)}
                                    >
                                        {tour.name}
                                    </Badge>
                                )}
                                <OriginIcon origin={tourOrigin(tour)} />
                            </CellTour>
                            <CellAppro>
                                <ApproPicker
                                    value={tour.deliveryPersonUserId ?? null}
                                    onChange={(deliveryPersonUserId) =>
                                        void actions.assignDeliveryPerson(tour, deliveryPersonUserId)
                                    }
                                    options={deliveryPersonOptions}
                                />
                            </CellAppro>
                            <CellStatut>
                                <StatutPill $color={STATUT_COLOR[statut]}>{statut}</StatutPill>
                            </CellStatut>
                            <Track
                                // Measured on a real lane, not on the header cell: the Row's borders make the
                                // header 2 px wider, and chips spread to the last pixel of THIS box.
                                ref={laneIndex === 0 ? trackRef : undefined}
                                {...dropHandlers(tour._id, droppable, () => {
                                    const dragged = draggedStop();
                                    if (!dragged || !dropAt) return;
                                    // Dropping inside its own lane: the stop is taken out before being spliced
                                    // back, so a rank counted on the unfiltered lane is one slot too far.
                                    const current = placed.findIndex((item) => item.stop._id === dragged.stop.stopId);
                                    const index =
                                        current >= 0 && current < dropAt.index ? dropAt.index - 1 : dropAt.index;
                                    void actions.moveStop(dragged.stop, dragged.label, dragged.from, tour, index);
                                })}
                                onDragOver={
                                    droppable
                                        ? (event) => {
                                              event.preventDefault();
                                              setDropTarget(tour._id);
                                              setDropAt({
                                                  key: tour._id,
                                                  ...dropPosition(
                                                      placed,
                                                      event.currentTarget.getBoundingClientRect(),
                                                      event.clientX,
                                                  ),
                                              });
                                          }
                                        : undefined
                                }
                            >
                                {hours.map((hour) => (
                                    <Grid key={hour} style={{ left: `${xOf(hour * 60)}%` }} />
                                ))}
                                {windowStartMin != null && windowStopMin != null && windowStopMin > windowStartMin && (
                                    <Band
                                        $color={color}
                                        style={{
                                            left: `${xOf(windowStartMin)}%`,
                                            width: `${xOf(windowStopMin) - xOf(windowStartMin)}%`,
                                        }}
                                    />
                                )}
                                {depot &&
                                    ([false, true] as const).map((isReturn) => {
                                        const min = isReturn ? depot.returnMin : depot.departureMin;
                                        const isClamped = isReturn ? min > axisMax : min < axisMin;
                                        const leftPx = depotFlagLeftPx({
                                            clockPct: xOf(min),
                                            isReturn,
                                            placed,
                                            trackWidth,
                                        });

                                        return (
                                            <DepotFlag
                                                key={isReturn ? 'back' : 'out'}
                                                $isReturn={isReturn}
                                                style={{ left: leftPx == null ? `${xOf(min)}%` : `${leftPx}px` }}
                                                onMouseEnter={(event) =>
                                                    setHoveredDepot({
                                                        tour,
                                                        depot,
                                                        isReturn,
                                                        isClamped,
                                                        rect: event.currentTarget.getBoundingClientRect(),
                                                    })
                                                }
                                                onMouseLeave={() => setHoveredDepot(null)}
                                            >
                                                <DepotHead $isReturn={isReturn}>
                                                    {isReturn ? (
                                                        <ArrowLeftToLine size={11} />
                                                    ) : (
                                                        <ArrowRightFromLine size={11} />
                                                    )}
                                                    <DepotHour>
                                                        {formatMinutes(Math.round(min))}
                                                        {isClamped ? ' ⟩' : ''}
                                                    </DepotHour>
                                                </DepotHead>
                                            </DepotFlag>
                                        );
                                    })}
                                {showNow && <NowLine style={{ left: `${xOf(nowMin)}%` }} />}
                                {dropAt?.key === tour._id && (
                                    <DropCaret style={{ left: `${dropAt.leftPx}px` }} data-rank={dropAt.index + 1} />
                                )}
                                {placed.map((item) => renderChip(item, { fromKey: tour._id }))}
                                {tour.stops.length === 0 && <EmptyLane>Aucun arrêt</EmptyLane>}
                            </Track>
                            <CellDetails>
                                {/* Same affordance as every list page: the clipboard opens the record. */}
                                <DetailsLink
                                    to={{ pathname: tourHref(tour._id), search }}
                                    data-test="more-details"
                                    title="Détails de la tournée"
                                >
                                    <IconClipboardList size={22} />
                                </DetailsLink>
                            </CellDetails>
                        </Row>
                    );
                })}

                {/* « À affecter »: deliveries in no tour. NOT a tour — no schedule, no time axis: a wrapping
                    list of named chips (88 loose deliveries once smeared the frise into a wall of dots). Drag
                    source AND drop target — dropping here releases a delivery from its tour. The whole row is
                    the drop zone. */}
                <LooseRow
                    $drop={dropTarget === LOOSE_LANE}
                    {...dropHandlers(LOOSE_LANE, Boolean(dragging && dragging.fromKey !== LOOSE_LANE), () => {
                        const dragged = draggedStop();
                        if (dragged?.from) void actions.removeStop(dragged.stop.stopId, dragged.label, dragged.from);
                    })}
                >
                    <CellTour>
                        <Badge type="button" $color={UNASSIGNED_STATUT_COLOR} $editable={false} title="À affecter">
                            À affecter · {unassignedDeliveries.length + unassignedTasks.length}
                        </Badge>
                    </CellTour>
                    <LooseList>
                        {unassignedDeliveries.map((delivery) => (
                            <LooseChip
                                key={delivery._id}
                                $state={getStopState(delivery)}
                                $dragging={dragging?.stopId === delivery._id}
                                $dimmed={isDimmed(delivery)}
                                draggable
                                onDragStart={(event) => {
                                    event.dataTransfer.effectAllowed = 'move';
                                    setHoveredLoose(null);
                                    setDragging({ stopId: delivery._id, fromKey: LOOSE_LANE });
                                }}
                                onDragEnd={endDrag}
                                onClick={() => navigate(deliveryHref(delivery._id))}
                                onMouseEnter={(event) =>
                                    setHoveredLoose({ delivery, rect: event.currentTarget.getBoundingClientRect() })
                                }
                                onMouseLeave={() => setHoveredLoose(null)}
                            >
                                <ChipLabel>{deliveryLabel(delivery)}</ChipLabel>
                                {hasFreefood(delivery) && <Apple size={11} strokeWidth={2.5} />}
                            </LooseChip>
                        ))}
                        {unassignedTasks.map((task) => {
                            return (
                                <LooseChip
                                    key={task._id}
                                    $square
                                    $state={
                                        task.state === TaskState.Finished
                                            ? DeliveryState.Delivered
                                            : task.state === TaskState.Aborted
                                              ? DeliveryState.Aborted
                                              : undefined
                                    }
                                    $dragging={dragging?.stopId === task._id}
                                    $dimmed={isDimmed(task)}
                                    draggable
                                    title={`${taskLabel(task)}\n${task.description}`}
                                    data-test="tour-timeline-loose-task"
                                    onDragStart={(event) => {
                                        event.dataTransfer.effectAllowed = 'move';
                                        setDragging({ stopId: task._id, fromKey: LOOSE_LANE });
                                    }}
                                    onDragEnd={endDrag}
                                    onClick={() => onOpenTask(task)}
                                >
                                    <ChipLabel>{taskLabel(task)}</ChipLabel>
                                    <TaskIcon size={11} />
                                </LooseChip>
                            );
                        })}
                        {unassignedDeliveries.length === 0 && unassignedTasks.length === 0 && (
                            <LooseHint>Déposez un arrêt ici pour le retirer de sa tournée</LooseHint>
                        )}
                    </LooseList>
                </LooseRow>

                {/* Empty draft lanes: drop a delivery to materialise the new tour server-side. */}
                {draftTours.map((draft) => (
                    <Row key={draft.key} $drop={dropTarget === draft.key}>
                        <CellTour>
                            <Badge type="button" $color="#64748b" $editable={false} title={draft.name}>
                                {draft.name}
                            </Badge>
                        </CellTour>
                        <CellAppro>
                            <DraftRemove
                                type="button"
                                title="Annuler cette tournée"
                                onClick={() =>
                                    setDraftTours((current) => current.filter((item) => item.key !== draft.key))
                                }
                            >
                                <X size={14} /> Annuler
                            </DraftRemove>
                        </CellAppro>
                        <CellStatut />
                        <Track
                            {...dropHandlers(draft.key, Boolean(dragging), () => {
                                void dropIntoDraft(draft.key, draft.name);
                            })}
                        >
                            <EmptyLane>Déposez un arrêt ici pour l'ajouter à « {draft.name} »</EmptyLane>
                        </Track>
                        {/* A draft has no tour to open yet — the cell only holds the grid together. */}
                        <CellDetails />
                    </Row>
                ))}
            </Body>

            {hoveredLoose && !dragging && (
                <HoverCard style={cardPosition(hoveredLoose.rect)}>
                    <TipTitle>{deliveryFullLabel(hoveredLoose.delivery)}</TipTitle>
                    <TipMeta>
                        {formatWindow(
                            deliveryWindow(hoveredLoose.delivery).start,
                            deliveryWindow(hoveredLoose.delivery).stop,
                        )}
                        {stateMeta(hoveredLoose.delivery)}
                    </TipMeta>
                    <TipMeta>
                        {hoveredLoose.delivery.boxes.length} caisse{hoveredLoose.delivery.boxes.length > 1 ? 's' : ''}
                        {deliveryZoneName(hoveredLoose.delivery) ? ` · ${deliveryZoneName(hoveredLoose.delivery)}` : ''}
                        {deliveryContractLabel(hoveredLoose.delivery)
                            ? ` · ${deliveryContractLabel(hoveredLoose.delivery)}`
                            : ''}
                    </TipMeta>
                    {deliveryContentSummary(hoveredLoose.delivery) && (
                        <TipMeta>{deliveryContentSummary(hoveredLoose.delivery)}</TipMeta>
                    )}
                    {stopAddress(hoveredLoose.delivery) && <TipMeta>{stopAddress(hoveredLoose.delivery)}</TipMeta>}
                    <TipLate>⚠ Sans tournée — à affecter</TipLate>
                </HoverCard>
            )}

            {hoveredDepot && !dragging && (
                <HoverCard style={cardPosition(hoveredDepot.rect)}>
                    <TipTitle>{hoveredDepot.isReturn ? 'Retour entrepôt' : 'Départ entrepôt'} · Gennevilliers</TipTitle>
                    <TipMeta>
                        {hoveredDepot.tour.name} ·{' '}
                        {formatMinutes(
                            Math.round(
                                hoveredDepot.isReturn ? hoveredDepot.depot.returnMin : hoveredDepot.depot.departureMin,
                            ),
                        )}{' '}
                        estimé
                    </TipMeta>
                    <TipMeta>
                        {hoveredDepot.isReturn
                            ? `${hoveredDepot.depot.roadBackMin} min de route après le dernier arrêt`
                            : `${DEPOT_SAS_MINUTES} min de sas + ${hoveredDepot.depot.roadOutMin} min de route jusqu'au premier arrêt`}
                    </TipMeta>
                    {hoveredDepot.isClamped && (
                        <TipLate>⚠ Hors de la frise — épinglé au bord, l'heure ci-dessus est la vraie</TipLate>
                    )}
                </HoverCard>
            )}

            {hovered && !dragging && (
                <HoverCard style={cardPosition(hovered.rect)}>
                    {isTask(hovered.placed.stop) ? (
                        <>
                            <TipTitle>
                                {hovered.placed.seq}. {taskLabel(hovered.placed.stop)}
                            </TipTitle>
                            <TipMeta>
                                {formatWindow(
                                    stopWindow(hovered.placed.stop).start,
                                    stopWindow(hovered.placed.stop).stop,
                                )}{' '}
                                · ≈ {formatMinutes(Math.round(hovered.placed.estimatedMin))} estimé ·{' '}
                                {TASK_STATE_LABELS[hovered.placed.stop.state]} ·{' '}
                                {formatDurationMinutes(hovered.placed.stop.theoricServiceTime)} sur place
                            </TipMeta>
                            <TipMeta>{hovered.placed.stop.description}</TipMeta>
                        </>
                    ) : (
                        <DeliveryHoverDetails placed={hovered.placed} delivery={hovered.placed.stop} />
                    )}
                </HoverCard>
            )}
        </Wrap>
    );
};

const DeliveryHoverDetails = ({ placed, delivery }: { placed: Scheduled; delivery: TourDelivery }) => (
    <>
        <TipTitle>
            {placed.seq}. {deliveryFullLabel(delivery)}
        </TipTitle>
        <TipMeta>
            ≈ {formatMinutes(Math.round(placed.estimatedMin))} estimé
            {placed.noWindow
                ? ' · créneau inconnu'
                : ` · Créneau ${formatMinutes(placed.windowStartMin)} → ${formatMinutes(placed.windowStopMin)}`}
            {stateMeta(delivery)}
            {deliveryContractLabel(delivery) ? ` · ${deliveryContractLabel(delivery)}` : ''}
        </TipMeta>
        {deliveryContentSummary(delivery) && <TipMeta>{deliveryContentSummary(delivery)}</TipMeta>}
        {placed.late && <TipLate>⚠ Passage estimé après la fin du créneau — à re-séquencer</TipLate>}
        {placed.squeezed && !placed.late && <TipLate>⚠ Tournée plus longue que son créneau — frise resserrée</TipLate>}
        {stopAddress(delivery) && <TipMeta>{stopAddress(delivery)}</TipMeta>}
    </>
);

const LANE_H = 44;
// Tournée | Appro | Statut | frise | détails
const GRID = css`
    display: grid;
    grid-template-columns: 140px 176px 120px minmax(0, 1fr) 32px;
    align-items: center;
    gap: 10px;
`;

const Wrap = styled.div`
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
`;

const DraftRemove = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    border: none;
    background: transparent;
    color: ${({ theme }) => theme.infoTextColor};
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    padding: 0;

    &:hover {
        color: ${({ theme }) => theme.errorColor};
    }
`;

const CellDetails = styled.div`
    display: flex;
    justify-content: center;
`;

const DetailsLink = styled(Link)`
    display: inline-flex;
    align-items: center;
    color: ${({ theme }) => theme.infoTextColor};

    &:hover {
        color: ${({ theme }) => theme.ctaPrimaryColor};
    }
`;

const HeaderRow = styled.div`
    ${GRID};
    padding: 0 12px 6px;
    flex-shrink: 0;
`;

const Th = styled.div`
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${({ theme }) => theme.infoTextColor};
`;

const ThTrack = styled.div`
    position: relative;
    height: 14px;
`;

const Tick = styled.span`
    position: absolute;
    top: 0;
    transform: translateX(-50%);
    font-size: 10px;
    font-weight: 700;
    color: ${({ theme }) => theme.infoTextColor};
    font-variant-numeric: tabular-nums;
`;

const Body = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const Row = styled.div<{ $drop: boolean }>`
    ${GRID};
    height: ${LANE_H}px;
    padding: 0 12px;
    background: ${({ theme }) => theme.cardBackgroundColor};
    border: 1px solid ${({ theme, $drop }) => ($drop ? theme.ctaPrimaryColor : theme.lightBorderColor)};
    border-radius: 10px;
    flex-shrink: 0;
    box-shadow: ${({ theme, $drop }) =>
        $drop ? `0 0 0 2px color-mix(in srgb, ${theme.ctaPrimaryColor} 35%, transparent)` : 'none'};
`;

const CellTour = styled.div`
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 6px;
`;

/** The « à affecter » pool: label + wrapping chip list, auto height — not aligned on the time grid. */
const LooseRow = styled.div<{ $drop: boolean }>`
    display: grid;
    grid-template-columns: 140px minmax(0, 1fr);
    align-items: start;
    gap: 10px;
    min-height: ${LANE_H}px;
    padding: 9px 12px;
    background: ${({ theme }) => theme.cardBackgroundColor};
    border: 1px solid ${({ theme, $drop }) => ($drop ? theme.ctaPrimaryColor : theme.lightBorderColor)};
    border-radius: 10px;
    flex-shrink: 0;
    box-shadow: ${({ theme, $drop }) =>
        $drop ? `0 0 0 2px color-mix(in srgb, ${theme.ctaPrimaryColor} 35%, transparent)` : 'none'};
`;

const LooseList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    min-height: 24px;
`;

/** A dragged chip is ghosted; a chip outside the visual filter fades further, so the matches stand out. */
const chipOpacity = ({ $dragging, $dimmed }: { $dragging: boolean; $dimmed: boolean }): number =>
    $dragging ? 0.35 : $dimmed ? 0.2 : 1;

/** The « à affecter » orange, which every chip of the pool keeps unless its state says otherwise. */
const LOOSE_COLOR = '#d97706';

/**
 * Only the states that take a delivery OUT of the dispatcher's hands break the pool's orange: green = already
 * delivered, grey = aborted or archived (nothing left to assign). Everything else is still « à livrer » and
 * stays orange — colouring the whole pool by state would drown those few in a wall of blue.
 */
const GREYED_STATES: DeliveryState[] = [DeliveryState.Aborted, DeliveryState.Archived];

const looseColor = (theme: Parameters<typeof toneColor>[0], state?: DeliveryState): string =>
    state === DeliveryState.Delivered
        ? toneColor(theme, 'success')
        : state && GREYED_STATES.includes(state)
          ? toneColor(theme, 'neutral')
          : LOOSE_COLOR;

const LooseChip = styled.button<{ $dragging: boolean; $dimmed: boolean; $state?: DeliveryState; $square?: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 3px;
    max-width: 220px;
    height: 24px;
    padding: 0 9px;
    border-radius: ${({ $square }) => ($square ? 0 : '999px')};
    color: ${({ theme }) => theme.textColor};
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    opacity: ${({ $dragging, $dimmed }) => chipOpacity({ $dragging, $dimmed })};
    cursor: grab;

    ${({ theme, $state }) => {
        const color = looseColor(theme, $state);
        return `
            border: 1px solid color-mix(in srgb, ${color} 45%, transparent);
            background: color-mix(in srgb, ${color} 12%, transparent);
            &:hover { border-color: ${color}; }
        `;
    }}
`;

const LooseHint = styled.span`
    font-size: 12px;
    color: ${({ theme }) => theme.infoTextColor};
    align-self: center;
`;

const Badge = styled.button<{ $color: string; $editable: boolean }>`
    display: inline-block;
    max-width: 100%;
    height: 22px;
    line-height: 22px;
    padding: 0 9px;
    border: none;
    border-radius: 6px;
    color: #fff;
    font-weight: 800;
    font-size: 11px;
    background: ${({ $color }) => $color};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: ${({ $editable }) => ($editable ? 'text' : 'default')};

    &:hover {
        filter: ${({ $editable }) => ($editable ? 'brightness(0.92)' : 'none')};
    }
`;

const NameInput = styled.input`
    width: 100%;
    height: 22px;
    padding: 0 7px;
    border-radius: 6px;
    border: 1px solid ${({ theme }) => theme.ctaPrimaryColor};
    background: ${({ theme }) => theme.cardBackgroundColor};
    color: ${({ theme }) => theme.textColor};
    font-size: 11px;
    font-weight: 700;
    outline: none;
`;

const CellAppro = styled.div`
    min-width: 0;
`;

const CellStatut = styled.div`
    min-width: 0;
`;

const StatutPill = styled.span<{ $color: string }>`
    font-size: 11px;
    font-weight: 700;
    padding: 3px 9px;
    border-radius: 999px;
    white-space: nowrap;
    color: ${({ $color }) => $color};
    background: ${({ $color }) => `color-mix(in srgb, ${$color} 13%, transparent)`};
`;

const Track = styled.div`
    position: relative;
    height: 100%;
`;

const Grid = styled.span`
    position: absolute;
    top: 4px;
    bottom: 4px;
    width: 1px;
    background: ${({ theme }) => theme.backgroundColor};
`;

/** The tour's window amplitude (earliest start → latest delivery) as a light tinted band behind the chips. */
const Band = styled.span<{ $color: string }>`
    position: absolute;
    top: 8px;
    bottom: 8px;
    border-radius: 999px;
    background: color-mix(in srgb, ${({ $color }) => $color} 12%, transparent);
    border: 1px solid color-mix(in srgb, ${({ $color }) => $color} 25%, transparent);
`;

/** Where the dragged chip lands, carrying the rank it will take — read before releasing the mouse. */
const DropCaret = styled.span`
    position: absolute;
    top: 1px;
    bottom: 1px;
    width: 3px;
    transform: translateX(-1px);
    border-radius: 2px;
    background: ${({ theme }) => theme.ctaPrimaryColor};
    z-index: 4;

    &::after {
        content: attr(data-rank);
        position: absolute;
        top: -1px;
        left: 50%;
        transform: translateX(-50%);
        padding: 0 4px;
        border-radius: 5px;
        background: ${({ theme }) => theme.ctaPrimaryColor};
        color: #fff;
        font-size: 9px;
        font-weight: 800;
        line-height: 12px;
    }
`;

/**
 * The warehouse legs, drawn not scheduled: leaving Gennevilliers before the first delivery, back after the last.
 * The line alone was invisible as soon as a chip landed on it, so each leg carries a labelled head pinned to
 * the TOP of the lane — above the chips, out of their way, and readable without hovering.
 */
const ChipLabel = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const DepotFlag = styled.span<{ $isReturn: boolean }>`
    position: absolute;
    top: 0;
    bottom: 0;
    width: 0;
    border-left: 2px solid ${({ theme }) => theme.infoTextColor};
    transform: translateX(-1px);
    z-index: 2;
    pointer-events: auto;
`;

const DepotHead = styled.span<{ $isReturn: boolean }>`
    position: absolute;
    top: -1px;
    ${({ $isReturn }) => ($isReturn ? 'right: 2px;' : 'left: 2px;')}
    display: flex;
    align-items: center;
    gap: 2px;
    padding: 1px 4px;
    border-radius: 4px;
    white-space: nowrap;
    color: ${({ theme }) => theme.backgroundColor};
    background: ${({ theme }) => theme.infoTextColor};
`;

const DepotHour = styled.span`
    font-size: 9px;
    font-weight: 800;
    line-height: 11px;
    font-variant-numeric: tabular-nums;
`;

const NowLine = styled.span`
    position: absolute;
    top: 2px;
    bottom: 2px;
    width: 2px;
    transform: translateX(-1px);
    background: ${({ theme }) => theme.errorColor};
    z-index: 1;
`;

/**
 * Left and width come from the layout pass — a DOT-wide pill IS the historical numbered dot.
 *
 * The FILL encodes the delivery state, never the tour: green delivered, blue en route, red cancelled, orange
 * paused, grey not delivered (to check). The tour's identity is already carried by its badge and its tinted band, and
 * colour is the only channel strong enough to read a whole day of statuses at a glance.
 */
const Chip = styled.button<{
    $tone: StateTone;
    $late: boolean;
    $dragging: boolean;
    $dimmed: boolean;
    $labelled: boolean;
    $square: boolean;
}>`
    position: absolute;
    top: ${(LANE_H - 2 - DOT) / 2}px;
    height: ${DOT}px;
    line-height: ${DOT - 4}px;
    /* A truncated name must not run into the pill's rounded edge; a numbered dot has no room to give. */
    box-sizing: border-box;
    padding: ${({ $labelled }) => ($labelled ? '0 5px' : '0')};
    border-radius: ${({ $square }) => ($square ? 0 : '999px')};
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1px;
    overflow: hidden;
    white-space: nowrap;
    text-align: center;
    /* Kept in sync with measureLabel: every chip's width is measured at exactly this font. */
    font-size: ${CHIP_FONT_SIZE_PX}px;
    font-weight: ${CHIP_FONT_WEIGHT};
    color: #fff;
    background: ${({ theme, $tone }) => toneColor(theme, $tone)};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
    border: 2px solid ${({ theme, $late }) => ($late ? theme.warningColor : 'transparent')};
    opacity: ${({ $dragging, $dimmed }) => chipOpacity({ $dragging, $dimmed })};
    cursor: grab;
    z-index: 2;

    &:hover {
        border-color: ${({ theme }) => theme.ctaPrimaryColor};
        z-index: 3;
    }
`;

/** Fixed → anchored to the hovered chip's rect, so it escapes the KPI strip and any scroll container. */
const HoverCard = styled.div`
    position: fixed;
    z-index: 1000;
    transform: translateX(-50%);
    min-width: 200px;
    max-width: 320px;
    padding: 8px 11px;
    border-radius: 9px;
    background: ${({ theme }) => theme.textColor};
    color: ${({ theme }) => theme.backgroundColor};
    box-shadow: 0 8px 22px rgba(0, 0, 0, 0.3);
    pointer-events: none;
`;

const TipTitle = styled.div`
    font-weight: 700;
    font-size: 12px;
`;

const TipMeta = styled.div`
    font-size: 11px;
    opacity: 0.85;
    font-variant-numeric: tabular-nums;
`;

const TipLate = styled.div`
    font-size: 11px;
    font-weight: 700;
    color: #fca5a5;
`;

const EmptyLane = styled.span`
    position: absolute;
    left: 4px;
    top: 12px;
    font-size: 12px;
    color: ${({ theme }) => theme.infoTextColor};
`;

const Empty = styled.div`
    padding: 40px;
    text-align: center;
    color: ${({ theme }) => theme.infoTextColor};
`;
